import { useMutation, useQueries, useQuery, useQueryClient } from '@tanstack/react-query';
import { adminClient } from '@/lib/adminClient';
import type { Profile } from '@/api/types';
import { userKeys } from './useUsers';

export const fflagKeys = {
  all: ['fflags'] as const,
  list: () => [...fflagKeys.all, 'list'] as const,
  overrides: (params: { userId?: string; fflagName?: string }) =>
    [...fflagKeys.all, 'overrides', params] as const,
};

export function useFFlags() {
  return useQuery({
    queryKey: fflagKeys.list(),
    queryFn: () => adminClient.listFFlags(),
  });
}

export function useFFlagOverrides(params: { userId?: string; fflagName?: string } = {}) {
  return useQuery({
    queryKey: fflagKeys.overrides(params),
    queryFn: () => adminClient.listFFlagOverrides(params),
  });
}

// Resolves user profiles for the given user IDs in parallel. Used to enrich
// override rows with email/firstName/lastName since the override only stores
// user_id. Cached against the same key as useUserDetail so navigating to a
// user detail page doesn't refetch.
export function useUserProfiles(userIds: string[]): Map<string, Profile | undefined> {
  const unique = Array.from(new Set(userIds.filter(Boolean)));
  const results = useQueries({
    queries: unique.map((id) => ({
      queryKey: userKeys.detail(id),
      queryFn: () => adminClient.getUser(id),
      enabled: !!id,
    })),
  });
  const map = new Map<string, Profile | undefined>();
  unique.forEach((id, i) => {
    map.set(id, results[i].data);
  });
  return map;
}

export function useEnableFFlag() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (name: string) => adminClient.enableFFlag(name),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: fflagKeys.all });
    },
  });
}

export function useDisableFFlag() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (name: string) => adminClient.disableFFlag(name),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: fflagKeys.all });
    },
  });
}

export function useSetFFlagOverride() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (params: { name: string; userId: string; enabled: boolean }) =>
      adminClient.setFFlagOverride(params),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: fflagKeys.all });
    },
  });
}

export function useDeleteFFlagOverride() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (params: { name: string; userId: string }) =>
      adminClient.deleteFFlagOverride(params),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: fflagKeys.all });
    },
  });
}
